import React from "react";

/**
 * BrimiLogo — Sun rising over clouds
 * Symbol of the dawn of a student's career journey.
 *
 * Props:
 *  - size: pixel size of the icon (default 36)
 *  - withBadge: wraps icon in a rounded gradient badge (default true)
 *  - showText: renders the BrimiAI wordmark next to the icon
 *  - textColor: color for wordmark (default white)
 *  - subtitle: small tag below the name (default "CareerCoach")
 *  - variant: "cream" (default) | "sky" | "dark"
 *  - animated: play sun-rise + ray-twinkle animations (default true)
 */
export default function BrimiLogo({
  size = 36,
  withBadge = true,
  showText = false,
  textColor = "white",
  subtitle = "CareerCoach",
  variant = "cream",
  animated = true,
  className = "",
}) {
  // Theming: warm dawn cream by default so the logo really pops on navy
  const themes = {
    cream:  { badge: "#FEF3C7", ray: "#F97316", cloud: "#0F1B33", horizon: "#0F1B33" },
    sky:    { badge: "#DBEAFE", ray: "#F97316", cloud: "#0C4A6E", horizon: "#0C4A6E" },
    dark:   { badge: "#0F1B33", ray: "#FBBF24", cloud: "#F8FAFC", horizon: "#F8FAFC" },
  };
  const t = themes[variant] || themes.cream;
  const gradId = `brimi-sun-${variant}`;

  const icon = (
    <svg
      viewBox="0 0 40 40"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      aria-label="BrimiAI logo"
      className={animated ? "brimi-logo-svg" : ""}
    >
      {withBadge && <rect width="40" height="40" rx="10" fill={t.badge} />}
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FBBF24" />
          <stop offset="100%" stopColor="#FB7185" />
        </linearGradient>
      </defs>

      {/* Sun rays — each animates independently with a soft twinkle */}
      <g stroke={t.ray} strokeWidth="1.6" strokeLinecap="round" className="brimi-rays">
        <line x1="20" y1="6"  x2="20" y2="9"  className="brimi-ray brimi-ray-1" />
        <line x1="11" y1="10" x2="13" y2="12" className="brimi-ray brimi-ray-2" />
        <line x1="29" y1="10" x2="27" y2="12" className="brimi-ray brimi-ray-3" />
        <line x1="6"  y1="18" x2="9"  y2="18" className="brimi-ray brimi-ray-4" />
        <line x1="34" y1="18" x2="31" y2="18" className="brimi-ray brimi-ray-5" />
      </g>

      {/* Sun rising from horizon — animates a gentle rise on mount */}
      <g className="brimi-sun">
        <path d="M11 24 A 9 9 0 0 1 29 24 Z" fill={`url(#${gradId})`} />
      </g>

      {/* Horizon line */}
      <line x1="6" y1="24.5" x2="34" y2="24.5" stroke={t.horizon} strokeWidth="0.8" strokeLinecap="round" opacity="0.85" />

      {/* Clouds / waves */}
      <path d="M6 29 Q 12 26.5, 18 29 T 34 29"    stroke={t.cloud} strokeWidth="1.4" fill="none" strokeLinecap="round" />
      <path d="M6 33.5 Q 14 31, 22 33.5 T 36 33.5" stroke={t.cloud} strokeWidth="1.1" fill="none" strokeLinecap="round" opacity="0.55" />
    </svg>
  );

  if (!showText) return <span className={className}>{icon}</span>;

  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      {icon}
      <span className="leading-none">
        <span className="font-display text-lg tracking-tight block" style={{ color: textColor }}>
          BrimiAI
        </span>
        {subtitle && (
          <span
            className="text-[10px] uppercase tracking-[0.2em] block mt-0.5"
            style={{ color: textColor, opacity: 0.75 }}
          >
            {subtitle}
          </span>
        )}
      </span>
    </span>
  );
}
